import { createWithEqualityFn } from 'zustand/traditional';
import { EnteredAddress } from '@models/validation';
import { ProductOptionSearchResponse } from '@lio/api-types';
import { Item } from '@components/select';
import { Customer } from '@models/customer';
import { IProductLabel } from '@services/productLabel';
import { ArrayItem } from '@utils/commonTypes';
import { InsuranceProductIds } from '@models/newQuote';

export type Scope = {
  brokerageAgency: Item | undefined;
  brokerageProducer: Item | undefined;
  productOffering: Item | undefined;
  insuranceProductId: string | undefined;
  productOption: ArrayItem<ProductOptionSearchResponse['items']> | undefined;
  productLabel: IProductLabel | undefined;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  writingCompany: { id: string; name: string } | undefined;
  isSpecialEvent: boolean | undefined;
};

export type NamedInsured = {
  customer: Customer | undefined;
};

export interface Location {
  insuredLocation: EnteredAddress;
}

type NewQuoteStore = {
  scope: Scope;
  namedInsured: NamedInsured;
  location: Location;
  updateScopeFields: (field: any) => void;
  updateNamedInsurance: (customer: Customer) => void;
  updateAddress: (addressToUpdate: EnteredAddress) => void;
  resetNextScopePanels: () => void;
  resetNextNameInsuredPanels: () => void;
  resetStorage: () => void;

  isFormLoading: boolean;
  setFormLoading: (isLoading: boolean) => void;
};

export const initialState = {
  scope: {
    brokerageAgency: undefined,
    brokerageProducer: undefined,
    productOffering: undefined,
    productLabel: undefined,
    insuranceProductId: undefined,
    lineBundle: undefined,
    productOption: undefined,
    effectiveDate: null,
    expirationDate: null,
    writingCompany: undefined,
    isSpecialEvent: undefined,
  },
  namedInsured: {
    customer: undefined,
  },
  location: {
    insuredLocation: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
    },
  },
  isFormLoading: false,
};

export const useNewQuoteStore = createWithEqualityFn<NewQuoteStore>()((set) => ({
  ...initialState,
  setFormLoading: (isLoading) => set(() => ({ isFormLoading: isLoading })),
  updateScopeFields: (field) =>
    set((prev) => ({
      ...prev,
      scope: {
        ...field,
        effectiveDate: typeof field.effectiveDate !== 'object' ? new Date(field.effectiveDate) : field.effectiveDate,
        expirationDate:
          typeof field.expirationDate !== 'object' ? new Date(field.expirationDate) : field.expirationDate,
        isSpecialEvent: field.insuranceProductId === InsuranceProductIds.SpecialEvents,
      },
    })),
  updateNamedInsurance: (customer) => {
    set(() => ({
      namedInsured: {
        customer: {
          ...customer,
          name: customer.name.trim(), // trim whitespace from name
        },
      },
    }));
  },
  updateAddress: (addressToUpdate) =>
    set(() => ({
      location: {
        insuredLocation: {
          ...addressToUpdate,
        },
      },
    })),
  resetNextScopePanels: () =>
    set(() => ({
      namedInsured: initialState.namedInsured,
      location: initialState.location,
    })),
  resetNextNameInsuredPanels: () =>
    set(() => ({
      location: initialState.location,
      namedInsured: {
        customer: undefined,
      },
    })),
  resetStorage: () => set(initialState),
}));
